import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getProjectSetupApprovalLabel,
  isProjectSetupReviewType
} from '../../utils/projectStructurePriorities';
import { formatRupees, SPEND_APPROVAL_TYPE } from './budget';
import {
  getTodaysSalesActivity,
  getTodaysSalesActivityForProjects
} from '../../services/api/implementation/impl';
import SalesActivityFeed from './SalesActivityFeed';

const spendApprovalLabel = (item) => {
  const expense = item?.items?.[0]?.expense;
  const taskName = expense?.task?.title || 'Management Task';
  return `Approve spend | ${taskName} | ${formatRupees(expense?.amount)}`;
};

const SALES_ACTIVITY_REFETCH_MS = 5 * 60 * 1000;

const extractActivities = (response, projectMeta = null) => {
  const payload = response?.data ?? response ?? {};
  const activities = Array.isArray(payload.activities) ? payload.activities : [];
  if (!projectMeta) return activities;
  return activities.map((activity) => ({
    ...activity,
    projectId: projectMeta.id,
    projectName: projectMeta.name
  }));
};

const mergeSalesActivities = (responsesWithMeta) => {
  const byId = new Map();
  responsesWithMeta.forEach(({ response, project }) => {
    extractActivities(response, project).forEach((activity) => {
      if (!activity?.id || byId.has(activity.id)) return;
      byId.set(activity.id, activity);
    });
  });
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.occurredAt || 0).getTime() - new Date(a.occurredAt || 0).getTime()
  );
};

const ActionCenterPanel = ({
  priorities,
  processPrioritiesData,
  filters,
  salesEscalations,
  engineerEscalations,
  inventoryTasks,
  getSalesEscalationsCount,
  getEngineerEscalationsCount,
  getInventoryEscalationsCount,
  shellSummary = null,
  onPriorityClick,
  onApprovalClick,
  onEscalationClick,
  panelHeight,
  showSalesActivityToggle = false,
  projectsForDropdown = []
}) => {
  const [panelMode, setPanelMode] = useState(
    showSalesActivityToggle ? 'salesActivity' : 'actionCenter'
  );
  const [actionCenterTab, setActionCenterTab] = useState('priorities');
  const hasProjectFilter = Boolean(filters?.projectID && filters.projectID !== '');
  const processedData = priorities
    ? processPrioritiesData(priorities?.data)
    : { priorities: [], approvals: [] };
  const prioritiesTabCount = processedData.priorities.reduce(
    (sum, p) => sum + (p.type === 'priority' ? p.count || 0 : 0),
    0
  );
  const approvalsTabCount = processedData.approvals.reduce(
    (sum, a) => sum + (a.type === 'approval' ? a.count || 0 : 0),
    0
  );
  // Sales escalations: prefer shell DB total (list is capped preview)
  const salesEscalationFromShell = Number(shellSummary?.salesEscalationCount);
  const salesEscalationCount =
    hasProjectFilter && Number.isFinite(salesEscalationFromShell) && salesEscalationFromShell >= 0
      ? salesEscalationFromShell
      : getSalesEscalationsCount(salesEscalations?.data);
  const escalationsTabCount =
    salesEscalationCount +
    getEngineerEscalationsCount(engineerEscalations?.data) +
    getInventoryEscalationsCount(inventoryTasks);
  const hasEscalations = escalationsTabCount > 0;
  const prioritiesListTotal = Number(priorities?.meta?.total);
  const prioritiesTruncated =
    (hasProjectFilter && shellSummary?.truncated) ||
    (Number.isFinite(prioritiesListTotal) &&
      Array.isArray(priorities?.data) &&
      prioritiesListTotal > priorities.data.length);

  const salesActivityProjects = useMemo(() => {
    if (hasProjectFilter) {
      const selected = projectsForDropdown.find(
        (p) => String(p.value ?? p.id) === String(filters.projectID)
      );
      return [
        {
          id: filters.projectID,
          name: selected?.label || selected?.name || null
        }
      ];
    }
    return (projectsForDropdown || [])
      .map((p) => ({
        id: p.value ?? p.id,
        name: p.label || p.name || null
      }))
      .filter((p) => p.id !== undefined && p.id !== null && String(p.id) !== '');
  }, [filters?.projectID, hasProjectFilter, projectsForDropdown]);

  const salesActivityProjectIds = salesActivityProjects.map((p) => String(p.id)).join(',');

  const {
    data: salesActivityResponse,
    isLoading: isLoadingSalesActivity,
    isFetching: isFetchingSalesActivity,
    isError: isSalesActivityError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['todaysSalesActivity', hasProjectFilter ? filters.projectID : 'all', salesActivityProjectIds],
    queryFn: async () => {
      if (hasProjectFilter && salesActivityProjects[0]) {
        const response = await getTodaysSalesActivity(salesActivityProjects[0].id);
        return {
          activities: mergeSalesActivities([{ response, project: salesActivityProjects[0] }]),
          date: response?.data?.date || response?.date || null,
          syncedAt: response?.data?.syncedAt || response?.syncedAt || new Date().toISOString()
        };
      }
      const projectIds = salesActivityProjects.map((p) => p.id);
      const response = await getTodaysSalesActivityForProjects(projectIds);
      const activities = (response?.data?.activities || response?.activities || [])
        .map((item) => ({
          ...item,
          projectId: item.project_id,
          projectName: item.project_name
        }))
        .sort(
          (a, b) =>
            new Date(b.occurredAt || 0).getTime() - new Date(a.occurredAt || 0).getTime()
        );
      return {
        activities,
        date: response?.data?.date || response?.date || null,
        syncedAt: response?.data?.syncedAt || response?.syncedAt || new Date().toISOString()
      };
    },
    enabled:
      showSalesActivityToggle &&
      panelMode === 'salesActivity' &&
      salesActivityProjects.length > 0,
    staleTime: 60_000,
    refetchInterval: SALES_ACTIVITY_REFETCH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData
  });

  const salesActivities = salesActivityResponse?.activities || [];
  const salesActivityDate = salesActivityResponse?.date || null;
  const salesActivitySyncedAt =
    salesActivityResponse?.syncedAt || (dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null);

  const renderPriorityList = (items, emptyTitle, emptyDescription) => {
    if (!items?.length) {
      return (
        <div className="text-center py-8 px-3">
          <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">{emptyTitle}</p>
          <p className="text-xs text-gray-500 mt-1">{emptyDescription}</p>
        </div>
      );
    }

    return items.map((item, index) => {
      if (item.type !== 'priority') return null;

      const displayLabel = item.priorityTitle || item.priorityType;
      const isOnboardingPriority =
        item.priorityType === 'Onboarding Company' || item.priorityType === 'Onboarding Project';
      const description = isOnboardingPriority ? item.items?.[0]?.description : null;
      const projectId = item.items?.[0]?.project_id || filters.projectID;
      const projectName = item.items?.[0]?.project?.name;

      return (
        <div
          key={`priority-${item.priorityTitle || item.priorityType}-${index}-${projectId}`}
          className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
          onClick={() => onPriorityClick(item, projectId)}>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                {displayLabel.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium text-gray-800">{displayLabel}</span>
                {description && (
                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-start gap-1">
              {item.items?.[0]?.tag &&
                (() => {
                  const tag = item.items[0].tag;
                  let tagClasses =
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

                  if (tag === 'Operations') {
                    tagClasses += ' bg-blue-100 text-blue-700 border border-blue-300';
                  } else if (tag === 'Sales') {
                    tagClasses += ' bg-green-100 text-green-700 border border-green-300';
                  } else if (tag === 'Management') {
                    tagClasses += ' bg-purple-100 text-purple-700 border border-purple-300';
                  } else {
                    tagClasses += ' bg-gray-100 text-gray-700 border border-gray-300';
                  }

                  return (
                    <div className="ml-8 mt-1">
                      <span className={tagClasses}>{tag}</span>
                    </div>
                  );
                })()}
              {!hasProjectFilter && projectName && (
                <div className=" mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                    {projectName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  const showActionCenterBody = !showSalesActivityToggle || panelMode === 'actionCenter';
  const showSalesActivityBody = showSalesActivityToggle && panelMode === 'salesActivity';

  return (
    <div
      className="flex h-full w-full min-w-0 flex-col bg-gray-100 shadow"
      style={{ height: panelHeight > 0 ? `${panelHeight}px` : 'calc(100vh - 6rem)' }}>
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white p-4 shadow-lg">
        <div className="shrink-0">
          {showSalesActivityToggle && (
            <div className="mb-3 grid w-full min-w-0 grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setPanelMode('salesActivity')}
                className={`min-w-0 rounded-lg px-2 py-2 text-center text-xs font-semibold leading-tight sm:text-sm ${
                  panelMode === 'salesActivity'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Sales Activity
              </button>
              <button
                type="button"
                onClick={() => setPanelMode('actionCenter')}
                className={`min-w-0 rounded-lg px-2 py-2 text-center text-xs font-semibold leading-tight sm:text-sm ${
                  panelMode === 'actionCenter'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Action Center ({prioritiesTabCount}
                {prioritiesTruncated ? '+' : ''})
              </button>
            </div>
          )}

          <h3 className="text-lg font-semibold text-[#1E4A53] mb-1">
            {showSalesActivityBody ? 'Sales Activity' : 'Action Center'}
          </h3>
          {showSalesActivityBody && (
            <p className="mb-3 text-xs text-gray-500">
              Today&apos;s sales movements
              {salesActivityDate ? ` · ${salesActivityDate}` : ''}
              {!hasProjectFilter && salesActivityProjects.length > 0
                ? ` · ${salesActivityProjects.length} projects`
                : ''}
            </p>
          )}

          {showActionCenterBody && (
            <div className="mb-4 grid w-full min-w-0 grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setActionCenterTab('priorities')}
                className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                  actionCenterTab === 'priorities'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Priorities ({prioritiesTabCount}
                {prioritiesTruncated ? '+' : ''})
              </button>
              <button
                type="button"
                onClick={() => setActionCenterTab('approvals')}
                className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                  actionCenterTab === 'approvals'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Approvals ({approvalsTabCount})
              </button>
              <button
                type="button"
                onClick={() => setActionCenterTab('escalations')}
                className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
                  actionCenterTab === 'escalations'
                    ? 'bg-white text-[#0A4A5C] shadow'
                    : 'text-gray-600 hover:bg-white/70'
                }`}>
                Escalations ({escalationsTabCount})
              </button>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {showSalesActivityBody && (
            <SalesActivityFeed
              className="flex min-h-0 flex-1 flex-col"
              activities={salesActivities}
              isLoading={isLoadingSalesActivity}
              isFetching={isFetchingSalesActivity}
              showProjectTags={!hasProjectFilter}
              hasProjects={salesActivityProjects.length > 0}
              error={isSalesActivityError}
              syncedAt={salesActivitySyncedAt}
              scrollable
            />
          )}

          {showActionCenterBody && (
            <div className="flex-1 overflow-y-auto pr-1">
          {actionCenterTab === 'priorities' && (
            <div className="mb-6 p-1">
              {renderPriorityList(
                processedData.priorities,
                'No priorities pending',
                'Nothing requires your action right now.'
              )}
            </div>
          )}

          {actionCenterTab === 'approvals' && (
            <div className="mb-6 p-1">
              {processedData.approvals.length > 0 ? (
                processedData.approvals.map((item, index) => {
                  if (item.type !== 'approval') return null;

                  let displayLabel;
                  if (item.priorityType === SPEND_APPROVAL_TYPE) {
                    displayLabel = spendApprovalLabel(item);
                  } else if (isProjectSetupReviewType(item.priorityType)) {
                    displayLabel = getProjectSetupApprovalLabel(item.priorityType);
                  } else if (item.priorityType === 'Deal Approval') {
                    displayLabel = 'Approve Closed Deal';
                  } else if (item.priorityType === 'Approve UC') {
                    displayLabel = 'Approve Under Consideration';
                  } else if (item.priorityType === 'Approve CCR') {
                    displayLabel = 'Approve Customer Change Request';
                  } else if (item.priorityType === 'Invoice Cancel') {
                    displayLabel = 'Approve Invoice Cancel';
                  } else if (hasProjectFilter) {
                    displayLabel = item.priorityType;
                  } else {
                    displayLabel = item.priorityTitle || item.priorityType;
                  }

                  const projectId =
                    item.projectId || item.items?.[0]?.project_id || filters.projectID;
                  const projectName = item.projectName || item.items?.[0]?.project?.name;
                  const isSpendApproval = item.priorityType === SPEND_APPROVAL_TYPE;

                  return (
                    <div
                      key={`approval-${
                        isSpendApproval
                          ? item.items?.[0]?.id
                          : hasProjectFilter
                            ? item.priorityType
                            : `${item.priorityType}-${projectId}`
                      }-${index}`}
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                      onClick={() => onApprovalClick(item, projectId)}>
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-semibold">
                            {displayLabel.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            {isSpendApproval
                              ? displayLabel
                              : `${displayLabel} | ${item.count} ${item.count === 1 ? 'task' : 'tasks'}`}
                          </span>
                        </div>
                        {(isSpendApproval || (!hasProjectFilter && projectName)) && (
                          <div className="ml-8 mt-1 flex flex-wrap gap-1">
                            {isSpendApproval && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-800 border border-teal-200">
                                Management task Expense
                              </span>
                            )}
                            {!hasProjectFilter && projectName && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                                {projectName}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 px-3">
                  <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700">No approvals pending</p>
                  <p className="text-xs text-gray-500 mt-1">You are all caught up for now.</p>
                </div>
              )}
            </div>
          )}

          {actionCenterTab === 'escalations' && (
            <div className="mb-6 p-1">
              {hasEscalations ? (
                <>
                  {salesEscalationCount > 0 && (
                    <div
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                      onClick={() => onEscalationClick('salesEscalations')}>
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-semibold">
                          E
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          Sales Escalations | {salesEscalationCount}{' '}
                          {salesEscalationCount === 1 ? 'task' : 'tasks'}
                        </span>
                      </div>
                    </div>
                  )}

                  {engineerEscalations &&
                    getEngineerEscalationsCount(engineerEscalations?.data) > 0 && (
                      <div
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                        onClick={() => onEscalationClick('engineerEscalations')}>
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-semibold">
                            E
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            Engineer Escalations |{' '}
                            {getEngineerEscalationsCount(engineerEscalations?.data)}{' '}
                            {getEngineerEscalationsCount(engineerEscalations?.data) === 1
                              ? 'task'
                              : 'tasks'}
                          </span>
                        </div>
                      </div>
                    )}

                  {inventoryTasks && getInventoryEscalationsCount(inventoryTasks) > 0 && (
                    <div
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                      onClick={() => onEscalationClick('inventoryEscalations')}>
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-semibold">
                          E
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          Inventory Escalations | {getInventoryEscalationsCount(inventoryTasks)}{' '}
                          {getInventoryEscalationsCount(inventoryTasks) === 1 ? 'task' : 'tasks'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 px-3">
                  <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700">No escalations right now</p>
                  <p className="text-xs text-gray-500 mt-1">Everything looks stable at the moment.</p>
                </div>
              )}
            </div>
          )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionCenterPanel;
